import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast, ToastContainer } from "react-toastify";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import "react-toastify/dist/ReactToastify.css";

import Breadcrumb from "../../../components/layout/Breadcrumb";
import {
  fetchDepartments,
  createDepartment,
  updateDepartment,
  activateDepartment,
  deactivateDepartment,
} from "../../../redux/slice/getDepartmentsSlice";
import { useHasPermission } from "../../../hooks/useHasPermission";

const EMPTY_FORM = { id: "", name: "", code: "" };

export default function DepartmentList() {
  const dispatch = useDispatch();
  const {
    list = [],
    loading,
    error,
    createLoading,
    updateLoading,
    statusUpdatingId,
  } = useSelector((state) => state.departments);

  const canView = useHasPermission("Department-view");
  const canManage = useHasPermission("Department-manage");

  const [sortField, setSortField] = useState("id");
  const [sortOrder, setSortOrder] = useState("asc");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", code: "" });
  const [editForm, setEditForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (canView) dispatch(fetchDepartments());
  }, [dispatch, canView]);

  const departments = useMemo(() => {
    return [...list].sort((a, b) => {
      let aValue = sortField === "status" ? (a.delete ? 0 : 1) : a?.[sortField];
      let bValue = sortField === "status" ? (b.delete ? 0 : 1) : b?.[sortField];
      if (typeof aValue === "string") aValue = aValue.toLowerCase();
      if (typeof bValue === "string") bValue = bValue.toLowerCase();
      if (aValue == null) aValue = "";
      if (bValue == null) bValue = "";
      if (aValue < bValue) return sortOrder === "asc" ? -1 : 1;
      if (aValue > bValue) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [list, sortField, sortOrder]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((current) => (current === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const handleCreate = () => {
    if (!canManage) {
      toast.error("You don't have permission to create departments");
      return;
    }
    setCreateForm({ name: "", code: "" });
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    if (createLoading) return;
    setShowCreateModal(false);
    setCreateForm({ name: "", code: "" });
  };

  const handleCreateChange = (event) => {
    const { name, value } = event.target;
    setCreateForm((current) => ({ ...current, [name]: value }));
  };

  const handleCreateSubmit = async (event) => {
    event.preventDefault();
    if (!canManage) {
      toast.error("You don't have permission to create departments");
      return;
    }

    const payload = {
      name: createForm.name.trim(),
      code: createForm.code.trim(),
    };
    if (!payload.name || !payload.code) {
      toast.error("Department name and code are required");
      return;
    }

    try {
      await dispatch(createDepartment(payload)).unwrap();
      toast.success("Department created successfully");
      setShowCreateModal(false);
      setCreateForm({ name: "", code: "" });
      await dispatch(fetchDepartments()).unwrap();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to create department");
    }
  };

  const handleEdit = (department) => {
    if (!canManage) {
      toast.error("You don't have permission to edit departments");
      return;
    }
    setEditForm({
      id: department.id,
      name: department.name || "",
      code: department.code || "",
    });
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    if (updateLoading) return;
    setShowEditModal(false);
    setEditForm(EMPTY_FORM);
  };

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEditForm((current) => ({ ...current, [name]: value }));
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!canManage) {
      toast.error("You don't have permission to edit departments");
      return;
    }

    const payload = {
      name: editForm.name.trim(),
      code: editForm.code.trim(),
    };
    if (!payload.name || !payload.code) {
      toast.error("Department name and code are required");
      return;
    }

    try {
      await dispatch(updateDepartment({ id: editForm.id, payload })).unwrap();
      toast.success("Department updated successfully");
      setShowEditModal(false);
      setEditForm(EMPTY_FORM);
      await dispatch(fetchDepartments()).unwrap();
    } catch (err) {
      toast.error(typeof err === "string" ? err : "Failed to update department");
    }
  };

  const handleStatusChange = async (department) => {
    if (!canManage) {
      toast.error("You don't have permission to manage departments");
      return;
    }

    const isInactive = Boolean(department.delete);
    const statusThunk = isInactive ? activateDepartment : deactivateDepartment;
    try {
      await dispatch(statusThunk(department.id)).unwrap();
      toast.success(`Department ${isInactive ? "activated" : "deactivated"} successfully`);
      await dispatch(fetchDepartments()).unwrap();
    } catch (err) {
      toast.error(
        typeof err === "string"
          ? err
          : `Failed to ${isInactive ? "activate" : "deactivate"} department`
      );
    }
  };

  if (!canView) {
    return <p className="perm-msg">You don't have permission to view Departments!</p>;
  }

  return (
    <div className="container-fluid" style={{ marginBottom: "30px" }}>
      <Breadcrumb />

      <div
        className="d-flex justify-content-between align-items-center"
        style={{ flexDirection: "row-reverse", marginBottom: "18px" }}
      >
        <div className="icons-list">
          <h5 className="mb-3 mt-1 d-none-m">Company Departments</h5>
          {canManage && (
            <button type="button" className="primary-btn ml-4" onClick={handleCreate}>
              <i className="fe fe-plus" /> + Create Department
            </button>
          )}
        </div>
      </div>

      <div className="section-body">
        <div className="container-fluid">
          <div className="tab-content">
            <div className="tab-pane fade active show">
              <div className="card-policy">
                <div className="card-body pt-0 pb-0">
                  <div className="announcement-container table-responsive m-0">
                    <table className="announcement-list table table-bordered table-hover table-vcenter text-nowrap mb-0">
                      <thead>
                        <tr>
                          <th onClick={() => handleSort("id")} style={{ cursor: "pointer", textAlign: "center" }}>
                            ID {sortField === "id" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                          </th>
                          <th onClick={() => handleSort("name")} style={{ cursor: "pointer" }}>
                            Department Name {sortField === "name" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                          </th>
                          <th onClick={() => handleSort("code")} style={{ cursor: "pointer" }}>
                            Code {sortField === "code" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                          </th>
                          <th onClick={() => handleSort("status")} style={{ cursor: "pointer", textAlign: "center" }}>
                            Status {sortField === "status" ? (sortOrder === "asc" ? "▲" : "▼") : ""}
                          </th>
                          <th style={{ textAlign: "center" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {error ? (
                          <tr><td colSpan={5} className="text-center"><p className="server-error-msg">{String(error)}</p></td></tr>
                        ) : loading ? (
                          <tr><td colSpan={5} className="text-center"><p className="loading-msg">Loading departments...</p></td></tr>
                        ) : departments.length === 0 ? (
                          <tr><td colSpan={5} className="text-center"><p className="server-error-msg">-- No department found --</p></td></tr>
                        ) : (
                          departments.map((item) => {
                            const inactive = Boolean(item.delete);
                            const isUpdating = String(statusUpdatingId) === String(item.id);
                            return (
                              <tr key={item.id}>
                                <td className="text-center">{item.id}</td>
                                <td>{item.name || "-"}</td>
                                <td>{item.code || "-"}</td>
                                <td className="text-center">
                                  <span className={`badge ${inactive ? "bg-danger" : "bg-success"}`}>
                                    {inactive ? "Inactive" : "Active"}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <PopupState variant="popover" popupId={`department-action-${item.id}`}>
                                    {(popupState) => (
                                      <>
                                        <button
                                          type="button"
                                          className="btn btn-icon btn-sm icons"
                                          {...bindTrigger(popupState)}
                                          title="Actions"
                                          disabled={isUpdating}
                                        >
                                          <img src={require("assets/img/more.png")} alt="actions" className="action-icon" />
                                        </button>
                                        <Menu {...bindMenu(popupState)}>
                                          {canManage && (
                                            <MenuItem onClick={() => { popupState.close(); handleEdit(item); }}>
                                              <img src={require("assets/img/edit-icon.png")} alt="Edit" className="action-icon me-2" style={{ width: 15 }} />
                                              Edit
                                            </MenuItem>
                                          )}
                                          {canManage && (
                                            <MenuItem onClick={() => { popupState.close(); handleStatusChange(item); }}>
                                              {inactive ? "Activate" : "Deactivate"}
                                            </MenuItem>
                                          )}
                                        </Menu>
                                      </>
                                    )}
                                  </PopupState>
                                </td>
                              </tr>
                            );
                          })
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CREATE DEPARTMENT MODAL */}
      {showCreateModal && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} onClick={closeCreateModal}>
          <div className="modal-dialog modal-dialog-centered" role="document" onClick={(event) => event.stopPropagation()}>
            <div className="modal-content">
              <form onSubmit={handleCreateSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">Create Department</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={closeCreateModal} disabled={createLoading} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="create-department-name">Department Name</label>
                    <input id="create-department-name" type="text" className="form-control" name="name" value={createForm.name} onChange={handleCreateChange} placeholder="Enter department name" maxLength={100} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="create-department-code">Department Code</label>
                    <input id="create-department-code" type="text" className="form-control" name="code" value={createForm.code} onChange={handleCreateChange} placeholder="Enter department code" maxLength={20} required />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeCreateModal} disabled={createLoading}>Cancel</button>
                  <button type="submit" className="primary-btn" disabled={createLoading}>{createLoading ? "Creating..." : "Save Department"}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* EDIT DEPARTMENT MODAL */}
      {showEditModal && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }} onClick={closeEditModal}>
          <div className="modal-dialog modal-dialog-centered" role="document" onClick={(event) => event.stopPropagation()}>
            <div className="modal-content">
              <form onSubmit={handleEditSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">Edit Department</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={closeEditModal} disabled={updateLoading} />
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label" htmlFor="edit-department-name">Department Name</label>
                    <input id="edit-department-name" type="text" className="form-control" name="name" value={editForm.name} onChange={handleEditChange} maxLength={100} required />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" htmlFor="edit-department-code">Department Code</label>
                    <input id="edit-department-code" type="text" className="form-control" name="code" value={editForm.code} onChange={handleEditChange} maxLength={20} required />
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={closeEditModal} disabled={updateLoading}>Cancel</button>
                  <button type="submit" className="primary-btn" disabled={updateLoading}>{updateLoading ? "Saving..." : "Save Changes"}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
}
